import { apiClient, publicClient, setToken } from './axiosInstance';
import { pickDefined, ensureOk, toFormData } from '../helpers/api-helper';
import { ApiEnvelope } from '@/types/apiEnvelope';
import type {
  LoginPayload,
  RegisterPayload,
  GetNewPasswordPayload,
  GetVerifyCodeByPhonePayload,
  GetVerifyCodeByEmailPayload,
  ChangePasswordPayload,
  LoginApiReturnData,
  GoogleLoginResponse,
  SafeVerifyByEmailOrPhonePayload,
  SafeVerifyAccountSetPayload,
} from '@/types/auth';
import { logout } from '@/features/auth/authSlice';
import type { AppDispatch } from '@/store/store';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { clearAuthStorage } from '../helpers/clearAuthStorage';

function persistAuth(result: LoginApiReturnData) {
  // ✅ token
  setToken(result.token);

  // ✅ tokenExpiresAt（到期時間點）
  const expiresInSec = result.expires_in ?? 3600;
  localStorage.setItem(STORAGE_KEYS.tokenExpiresAt, String(Date.now() + expiresInSec * 1000));

  // ✅ refreshToken
  if (result.refreshToken) {
    localStorage.setItem(STORAGE_KEYS.refreshToken, result.refreshToken);
  }

  // ✅ meInfo
  localStorage.setItem(STORAGE_KEYS.meInfo, JSON.stringify(result));
}

export const AuthService = {
  /*----------- 登入 -----------*/
  // LINE登入
  async loginByLine(): Promise<string> {
    const res = await publicClient.get('/line/redirect');
    const result = res.data;
    const loginUrl = result?.data;

    if (!loginUrl || typeof loginUrl !== 'string') {
      throw new Error('LINE redirect failed: unexpected response format');
    }
    return loginUrl;
  },

  async LineTokenGetMeInfo(token: string) {
    // 這裡 token 是從 LINE callback 拿到的 access token
    setToken(token);

    const { data } = await apiClient.get<ApiEnvelope<LoginApiReturnData>>('/me/simple');
    const result = ensureOk<LoginApiReturnData>(data);

    // ✅ 確保 result 補上 token（你後面會丟進 Redux setCredentials）
    result.token = token;
    persistAuth(result);
    return result;
  },

  // google登入
  async loginByGoogle(idToken: string) {
    // 後端期望的 key 是 "token"
    const payload = pickDefined({ token: idToken });
    const { data } = await publicClient.post<GoogleLoginResponse>('/google/login', payload);
    const result = ensureOk<LoginApiReturnData>(data);
    persistAuth(result);
    return result;
  },

  // 手機或信箱登入
  async loginByPhoneOrEmail(payload: LoginPayload) {
    const params = {
      phone: 'phone' in payload ? payload.phone : undefined,
      email: 'email' in payload ? payload.email : undefined,
      password: payload.password,
    };
    const { data } = await publicClient.get<ApiEnvelope<LoginApiReturnData>>('/auth/login', {
      params,
    });
    const result = ensureOk<LoginApiReturnData>(data);
    persistAuth(result);
    return result;
  },

  /*----------- 忘記密碼 -----------*/
  // 取得新密碼，透過手機或信箱
  async getNewPasswordByPhoneOrEmail(payload: GetNewPasswordPayload) {
    const fd = toFormData({
      phone: 'phone' in payload ? payload.phone : undefined,
      email: 'email' in payload ? payload.email : undefined,
    });
    await publicClient.post('/forget_password', fd);
  },

  /*----------- 修改密碼 -----------*/
  async changePassword(payload: ChangePasswordPayload) {
    const fd = toFormData({
      old_password: payload.old_password,
      new_password: payload.new_password,
      again_new_password: payload.again_new_password,
    });
    await apiClient.post('/me/password', fd);
  },

  /*----------- 註冊 -----------*/
  async registerByPhoneOrEmail(payload: RegisterPayload) {
    console.log(payload);

    const base = {
      image: payload.image,
      name: payload.name,
      password: payload.password,
      again_password: payload.again_password,
    };

    const fd =
      'phone' in payload
        ? toFormData({
            ...base,
            phone: payload.phone,
            verification: payload.verification, // ✅ 手機驗證碼
          })
        : toFormData({
            ...base,
            email: payload.email,
            email_verification: payload.email_verification, // ✅ 信箱驗證碼
          });

    const { data } = await publicClient.post<ApiEnvelope<LoginApiReturnData>>('/auth/register', fd);
    const result = ensureOk<LoginApiReturnData>(data);

    persistAuth(result);
    return result;
  },

  //註冊驗證碼，透過手機
  async getRegisterCodeByPhone(payload: GetVerifyCodeByPhonePayload) {
    const fd = toFormData({ phone: payload.phone });
    await publicClient.post('/sms_send', fd);
  },

  // 註冊驗證碼，透過信箱
  async getRegisterCodeByEmail(payload: GetVerifyCodeByEmailPayload) {
    const fd = toFormData({ email: payload.email });
    await publicClient.post('/register_send_email', fd);
  },

  /*----------- 登出 -----------*/
  handleLogout(dispatch: AppDispatch) {
    dispatch(logout());
    clearAuthStorage();
  },

  /*----------- 安全認證 -----------*/
  async safeVerifyByEmail(payload: SafeVerifyByEmailOrPhonePayload) {
    const fd = toFormData({ email: payload.email });
    await apiClient.post('/me/verify_email', fd);
  },

  async safeVerifyByPhone(payload: SafeVerifyByEmailOrPhonePayload) {
    const fd = toFormData({ phone: payload.phone });
    await apiClient.post('/me/verify_phone', fd);
  },

  async safeVerifyAccount(payload: SafeVerifyAccountSetPayload) {
    const fd = toFormData({ type: payload.type, token: payload.token });
    await apiClient.post('/me/verify_account_set', fd);
  },
};
